import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { MessageResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { groupBy } from "@redotech/util/map";
import { useMemo } from "react";
import { useSingleConversationData } from "./use-conversation-data";

export interface ThreadWithMessages {
  thread: ConversationThreadResponse;
  messages: MessageResponse[];
}

export function useConversationView(conversationId: ConversationId) {
  const { conversation, threads, messages, isLoading } =
    useSingleConversationData(conversationId);

  const messagesByThreadId = useMemo(
    () => groupBy(messages, (msg) => msg.conversationThreadId),
    [messages],
  );

  const threadsWithMessages = useMemo(
    (): ThreadWithMessages[] =>
      threads.map((thread) => ({
        thread,
        messages: messagesByThreadId.get(thread.id) ?? [],
      })),
    [threads, messagesByThreadId],
  );

  // TODO: Fetch actual customer name from Customer model using conversation.mongoCustomerId
  const customerName = "Customer";

  const latestMessageTime = useMemo(() => {
    if (messages.length === 0) {
      return null;
    }
    const sorted = [...messages].sort(
      (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime(),
    );
    return new Date(sorted[0].sentAt);
  }, [messages]);

  return {
    conversation,
    threads,
    threadsWithMessages,
    messages,
    isLoading,
    customerName,
    latestMessageTime,
  };
}
